import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Award } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function FranchiseFormats() {
  const { t } = useLang();
  const f = t.franchiseFormats;

  return (
    <section id="franchise-formats" className="py-32 md:py-40 relative overflow-hidden" style={{ background: '#0A0B0F' }}>
      <div
        className="orb-gold absolute"
        style={{ width: 800, height: 800, bottom: '-25%', right: '-15%', opacity: 0.15 }}
      />
      <div
        className="orb-purple absolute"
        style={{ width: 600, height: 600, top: '-15%', left: '-10%', opacity: 0.12 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-bold">Franchise</span>
          </div>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-heading leading-[0.95]" style={{ fontFamily: "'Sora', sans-serif", textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>
            {f.title}
          </h2>
          <p className="text-[#BFC3C9] text-xl md:text-2xl">{f.subtitle}</p>
        </motion.div>

        {/* Three Premium Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Standard Card */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[2rem] p-10 md:p-12 card-lift group cursor-default"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0.03) 100%)',
              border: '1px solid rgba(212,175,55,0.3)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(212,175,55,0.2)'
            }}
            data-testid="franchise-card-standard"
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
              style={{ background: 'radial-gradient(circle at 50% 0%, rgba(212,175,55,0.2) 0%, transparent 70%)' }}
            />

            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-full px-5 py-2 mb-8 relative">
              <TrendingUp size={16} className="text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs font-black tracking-wider uppercase">{f.standard.badge}</span>
            </div>

            <h3 className="text-lg sm:text-xl md:text-2xl lg:text-2xlfont-black font-heading gold-text mb-3 relative leading-tight min-h-[3rem] md:min-h-[3.5rem]" style={{ wordBreak: 'keep-all', hyphens: 'none' }}>{f.standard.type}</h3>
            <p className="text-[#BFC3C9] text-lg md:text-xl mb-3 relative">{f.standard.area}</p>
            <p className="text-[#9CA3AF] text-sm mb-8 relative">{f.standard.desc}</p>

            <div className="space-y-4 relative">
              {[
                { label: f.standard.label1, val: f.standard.val1 },
                { label: f.standard.label2, val: f.standard.val2 },
                { label: f.standard.label3, val: f.standard.val3 },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-4 border-b border-white/[0.08] last:border-0">
                  <span className="text-[#9CA3AF] text-sm md:text-base">{item.label}</span>
                  <span className="text-white font-black font-heading text-lg md:text-xl whitespace-nowrap ml-4">{item.val}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Recommended Card */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[2rem] p-10 md:p-12 card-lift group cursor-default"
            style={{
              background: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(34,197,94,0.03) 100%)',
              border: '1px solid rgba(34,197,94,0.3)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(34,197,94,0.2)'
            }}
            data-testid="franchise-card-recommended"
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
              style={{ background: 'radial-gradient(circle at 50% 0%, rgba(34,197,94,0.2) 0%, transparent 70%)' }}
            />

            <div className="inline-flex items-center gap-2 bg-[#22C55E]/15 border border-[#22C55E]/40 rounded-full px-5 py-2 mb-8 relative">
              <Award size={16} className="text-[#22C55E]" />
              <span className="text-[#22C55E] text-xs font-black tracking-wider uppercase">{f.recommended.badge}</span>
            </div>

            <h3 className="text-lg sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl font-black font-heading mb-3 relative leading-tight min-h-[3rem] md:min-h-[3.5rem]" style={{ color: '#22C55E', wordBreak: 'keep-all', hyphens: 'none' }}>
              {f.recommended.type}
            </h3>
            <p className="text-[#BFC3C9] text-lg md:text-xl mb-3 relative">{f.recommended.area}</p>
            <p className="text-[#9CA3AF] text-sm mb-8 relative">{f.recommended.desc}</p>

            <div className="space-y-4 relative">
              {[
                { label: f.recommended.label1, val: f.recommended.val1 },
                { label: f.recommended.label2, val: f.recommended.val2 },
                { label: f.recommended.label3, val: f.recommended.val3 },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-4 border-b border-white/[0.08] last:border-0">
                  <span className="text-[#9CA3AF] text-sm md:text-base">{item.label}</span>
                  <span className="text-white font-black font-heading text-lg md:text-xl whitespace-nowrap ml-4">{item.val}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Premium Card */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[2rem] p-10 md:p-12 card-lift-purple group cursor-default"
            style={{
              background: 'linear-gradient(135deg, rgba(107,78,255,0.15) 0%, rgba(107,78,255,0.05) 100%)',
              border: '1px solid rgba(107,78,255,0.35)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(107,78,255,0.3)'
            }}
            data-testid="franchise-card-premium"
          >
            {/* Glow on hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
              style={{ background: 'radial-gradient(circle at 50% 0%, rgba(107,78,255,0.25) 0%, transparent 70%)' }}
            />

            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#6B4EFF]/15 border border-[#6B4EFF]/40 rounded-full px-5 py-2 mb-8 relative">
              <Award size={16} className="text-[#9B7FFF]" />
              <span className="text-[#9B7FFF] text-xs font-black tracking-wider uppercase">{f.premium.badge}</span>
            </div>

            {/* Type */}
            <h3 className="text-lg sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl font-black font-heading purple-text mb-3 leading-tight relative min-h-[3rem] md:min-h-[3.5rem]" style={{ wordBreak: 'keep-all', hyphens: 'none' }}>
              {f.premium.type}
            </h3>
            <p className="text-[#BFC3C9] text-lg md:text-xl mb-3 relative">{f.premium.area}</p>
            <p className="text-[#9CA3AF] text-sm mb-8 relative">{f.premium.desc}</p>

            {/* Metrics */}
            <div className="space-y-4 relative">
              {[
                { label: f.premium.label1, val: f.premium.val1 },
                { label: f.premium.label2, val: f.premium.val2 },
                { label: f.premium.label3, val: f.premium.val3 },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-4 border-b border-white/[0.08] last:border-0">
                  <span className="text-[#9CA3AF] text-sm md:text-base">{item.label}</span>
                  <span className="text-white font-black font-heading text-lg md:text-xl whitespace-nowrap ml-4">{item.val}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
